"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const uuid_1 = require("uuid");
const errors_1 = require("@seneca/errors");
const BadRequestError_1 = __importDefault(require("./errors/BadRequestError"));
const RequestLimitExceeded_1 = __importDefault(require("./errors/RequestLimitExceeded"));
const UnauthorisedResponseError_1 = __importDefault(require("./errors/UnauthorisedResponseError"));
const UnexpectedServerResponse_1 = __importDefault(require("./errors/UnexpectedServerResponse"));
const UnrectifiableClockSkewError_1 = __importDefault(require("./errors/UnrectifiableClockSkewError"));
const formatMillisAsAmazonDate_1 = __importDefault(require("./utils/formatMillisAsAmazonDate"));
const isValidFunction_1 = __importDefault(require("./utils/isValidFunction"));
class RequestMaker {
    constructor({ fetch, getCredentials, options = {} }) {
        if (!fetch) {
            throw new errors_1.MissingArgumentError('fetch');
        }
        // We need to bind fetch to the executing context.
        // In a browser, this will bind it to the window object
        this.fetch = fetch.bind(undefined);
        this.region = options.region;
        this.correlationId = options.correlationId;
        this.traceLogging = options.traceLogging || false;
        this.signal = options.signal || undefined;
        this.getUserRegion = options.getUserRegion;
        this.getUserLanguage = options.getUserLanguage;
        this.log = options.log || console.log;
        this.logError = options.logError || console.error;
        this.interceptUnauthorisedResponse = options.interceptUnauthorisedResponse;
        this.retryUnauthorisedResponse = options.retryUnauthorisedResponse || false;
        this.retries =
            options.retries === undefined ||
                options.retries === null ||
                Number.isNaN(options.retries)
                ? 3
                : options.retries;
        if (options.delayInSecs === null || options.delayInSecs === undefined) {
            this.delay = 2000;
        }
        else {
            this.delay = options.delayInSecs * 1000;
        }
        this.clockskew = 0;
        this.getCredentials = getCredentials;
    }
    static _isAmazonClockSkewError(response) {
        const errorTypeHeader = response && response.headers && response.headers.get('x-amzn-errortype');
        if (!errorTypeHeader) {
            return false;
        }
        return [
            'RequestTimeTooSkewed',
            'RequestExpired',
            'InvalidSignatureException',
            'SignatureDoesNotMatch',
            'AuthFailure',
            'RequestInTheFuture'
        ].includes(errorTypeHeader);
    }
    setClockSkewFromAmazonResponse(response, correlationId) {
        const dateHeader = response.headers && response.headers.get('date');
        if (!dateHeader) {
            throw new UnrectifiableClockSkewError_1.default({
                reason: 'Server returned no date header',
                correlationId
            });
        }
        const serverDate = new Date(dateHeader).getTime();
        if (isNaN(serverDate)) {
            throw new UnrectifiableClockSkewError_1.default({
                reason: 'Server returned non-parsable date',
                correlationId
            });
        }
        this.clockskew = serverDate - new Date().getTime();
    }
    generateClockSkewAdjustedAmazonDate() {
        return formatMillisAsAmazonDate_1.default(new Date().getTime() + this.clockskew);
    }
    retryRequest(method, url, options, expectedResponses, count, fetchError, response, delay) {
        return __awaiter(this, void 0, void 0, function* () {
            if (count >= this.retries) {
                throw new RequestLimitExceeded_1.default({
                    method,
                    url,
                    fetchError,
                    body: response ? response.text : undefined,
                    status: response ? response.status : undefined,
                    correlationId: options.headers.correlationId
                });
            }
            yield new Promise(resolve => setTimeout(resolve, delay));
            return this.makeRequest(url, options, expectedResponses, count + 1, delay * 2);
        });
    }
    getRegionHeader(options) {
        if (options.noRegion) {
            return undefined;
        }
        if (options.region) {
            return options.region;
        }
        else if (this.getUserRegion && isValidFunction_1.default(this.getUserRegion)) {
            return this.getUserRegion();
        }
        return undefined;
    }
    getLanguageHeader(options) {
        if (options.noLanguage) {
            return undefined;
        }
        if (options.language) {
            return options.language;
        }
        else if (this.getUserLanguage && isValidFunction_1.default(this.getUserLanguage)) {
            return this.getUserLanguage();
        }
        return undefined;
    }
    makeRequest(url, options = {}, expectedResponses = [200], count = 0, delay = this.delay, interceptUnauthorisedResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            const method = options.method || 'GET';
            const correlationId = this.correlationId || this.generateCorrelationId();
            options.headers = Object.assign({
                correlationId,
                'Content-Type': 'application/json'
            }, options.headers || {});
            options.headers['X-Amz-Date'] = this.generateClockSkewAdjustedAmazonDate();
            if (this.signal) {
                options.signal = this.signal;
            }
            const regionHeader = this.getRegionHeader(options);
            const languageHeader = this.getLanguageHeader(options);
            if (regionHeader) {
                options.headers = Object.assign(Object.assign({}, options.headers), { 'user-region': this.getRegionHeader(options) });
            }
            if (languageHeader) {
                options.headers = Object.assign(Object.assign({}, options.headers), { 'user-language': this.getLanguageHeader(options) });
            }
            if (this.getCredentials) {
                const credentials = yield this.getCredentials();
                if (credentials && credentials.accessToken) {
                    options.headers = Object.assign({ 'access-key': credentials.accessToken }, options.headers);
                }
            }
            let serverResponse;
            let response = {};
            try {
                if (this.traceLogging) {
                    this.log('Making request', { method, url, body: options.body });
                }
                serverResponse = yield this.fetch(url, options);
                response.status = serverResponse.status;
                response.text = yield serverResponse.text();
                response.headers = serverResponse.headers;
                if (response.text &&
                    ['Content-Type', 'content-type'].some(header => serverResponse.headers.get(header) &&
                        serverResponse.headers.get(header).includes('application/json'))) {
                    response.json = JSON.parse(response.text);
                }
                if (this.traceLogging) {
                    this.log('Received response', { method, url, body: response.text });
                }
            }
            catch (err) {
                if (this.retries === 0) {
                    throw err;
                }
                return this.retryRequest(method, url, options, expectedResponses, count, err, null, delay);
            }
            const status = response.status;
            if (status === 429 || (status >= 500 && status <= 599)) {
                return this.retryRequest(method, url, options, expectedResponses, count, null, response, delay);
            }
            if (RequestMaker._isAmazonClockSkewError(response)) {
                this.setClockSkewFromAmazonResponse(response, correlationId);
                return this.retryRequest(method, url, options, expectedResponses, count, null, response, delay);
            }
            if (!expectedResponses.includes(status)) {
                if (status === 400) {
                    throw new BadRequestError_1.default(method, url, status, correlationId);
                }
                if (status === 401) {
                    if (this.retryUnauthorisedResponse && count < this.retries) {
                        return this.retryRequest(method, url, options, expectedResponses, count, null, response, delay);
                    }
                    if (interceptUnauthorisedResponse) {
                        return interceptUnauthorisedResponse({
                            response,
                            requestOptions: options,
                            url
                        });
                    }
                    else if (this.interceptUnauthorisedResponse) {
                        return this.interceptUnauthorisedResponse({
                            response,
                            requestOptions: options,
                            url
                        });
                    }
                    throw new UnauthorisedResponseError_1.default({ method, url, correlationId });
                }
                throw new UnexpectedServerResponse_1.default(method, url, status, correlationId);
            }
            return response;
        });
    }
    generateCorrelationId() {
        return `${new Date().getTime()}::${uuid_1.v4()}`;
    }
}
exports.default = RequestMaker;
